import { PromiseCompletionSource } from "scene-manager";
import { BaseLoseSceneView } from "../BaseLoseSceneView";
import { LoseSceneResourcesPackage } from "../../resources/LoseSceneResourcesPackage";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { Text } from "@pixi/text";
import { LoseController } from "../../controller/LoseController";
import { Container, IDestroyOptions } from "@pixi/display";
import { FitText } from "../../../common/components/FitText";
import { Localization } from "../../../common/Localization";
import { AudioManager } from "../../../../audio/AudioManager";
import { PinkTextStyle, YellowTextStyle } from "../../resources/LoseTextStyles";
import { Emitter, upgradeConfig } from "@pixi/particle-emitter";
import { Spine } from "pixi-spine";

export class LoseSceneView extends BaseLoseSceneView {
    private particles: Emitter;
    private particlesContainer: Container;
    private circleAnimation: Spine;
    private gameOver: FitText;
    private yourScoreText: FitText;
    private playerScore: Text;
    private bestScoreText: FitText;
    private bestPlayerScore: Text;

    public constructor(
        canvasScaler: ICanvasScaler,
        loseSceneResourcesPackage: LoseSceneResourcesPackage,
        loseController: LoseController,
        localization: Localization,
        audioManager: AudioManager,
        operationControls: PromiseCompletionSource<void>
    ) {
        const backgroundTexture = window.maxTextureSize <= 4096
            ? loseSceneResourcesPackage.assets_lose_background_pink_mobile_png :
            loseSceneResourcesPackage.assets_lose_background_pink_png;
        super(
            canvasScaler,
            loseSceneResourcesPackage.assets_lose_background_pink_png,
            loseSceneResourcesPackage,
            loseController,
            localization,
            audioManager,
            operationControls
        );
    }

    protected build(): void {
        super.build();
        const config = upgradeConfig(this.loseSceneResourcesPackage.assets_lose_effects_particles_emitter_json as any,
            [
                this.loseSceneResourcesPackage.assets_lose_effects_particles_dot_1_png,
                this.loseSceneResourcesPackage.assets_lose_effects_particles_dot_2_png,
                this.loseSceneResourcesPackage.assets_lose_effects_particles_dot_3_png
            ]);
        this.particlesContainer = new Container();
        this.addChild(this.particlesContainer);
        this.particles = new Emitter(this.particlesContainer, config);
        this.particles.autoUpdate = true;

        this.circleAnimation = new Spine((this.loseSceneResourcesPackage.assets_lose_effects_win_glow_win_glow_json as any).spineData);
        this.circleAnimation.state.setAnimation(0, "win_glow", true);
        this.addChild(this.circleAnimation);

        this.gameOver = new FitText(this.localization.getText("game_over"), new PinkTextStyle(105), 720, 195);
        this.addChild(this.gameOver);
        this.gameOver.anchor.set(0.5);

        this.yourScoreText = new FitText(this.localization.getText("your_moves"), new PinkTextStyle(94), 380, 85);
        this.addChild(this.yourScoreText);
        this.yourScoreText.anchor.set(0.5);

        this.playerScore = new Text(this.loseController.getPlayerScore(), new PinkTextStyle());
        this.addChild(this.playerScore);
        this.playerScore.anchor.set(0.5);

        this.bestScoreText = new FitText(this.localization.getText("best_moves"), new YellowTextStyle(65, "pink"), 380, 85);
        this.addChild(this.bestScoreText);
        this.bestScoreText.anchor.set(0.5);

        this.bestPlayerScore = new Text(this.loseController.getBestScore(), new YellowTextStyle(150, "pink"));
        this.addChild(this.bestPlayerScore);
        this.bestPlayerScore.anchor.set(0.5);
    }

    protected layout(): void {
        super.layout();
        const referenceWidth = this.canvasScaler.referenceWidth;
        this.gameOver.position.set(referenceWidth * 0.5, 327 + (this.gameOver.height * 0.5));
        this.yourScoreText.position.set(referenceWidth * 0.5, 618 + (this.yourScoreText.height * 0.5));
        this.playerScore.position.set(referenceWidth * 0.5, 701 + (this.playerScore.height * 0.5));
        this.bestScoreText.position.set(referenceWidth * 0.5, 966 + (this.bestScoreText.height * 0.5));
        this.bestPlayerScore.position.set(referenceWidth * 0.5, 1107.5 + (this.bestScoreText.height * 0.5));
        this.particlesContainer.position.set(referenceWidth * 0.5, this.playerScore.position.y);
        this.circleAnimation.position.set(referenceWidth * 0.5, this.playerScore.position.y);
    }

    public destroy(options?: IDestroyOptions | boolean): void {
        this.particles.destroy();
        super.destroy(options);
    }
}
